import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Search, CheckSquare, Square, Users, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCOPE_COLOR = {
  'Super User':          'bg-purple-100 text-purple-700',
  'Reseller':            'bg-blue-100 text-blue-700',
  'Office Manager':      'bg-teal-100 text-teal-700',
  'Call Center Agent':   'bg-amber-100 text-amber-700',
  'Basic User':          'bg-slate-100 text-slate-600',
};

const YabbitUsersModal = ({ open, onClose, onSaved }) => {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/yabbit/users`),
      axios.get(`${API}/yabbit/selected-users`),
    ])
      .then(([usersRes, selRes]) => {
        setUsers(usersRes.data || []);
        setSelected(new Set(selRes.data.selected_users || []));
      })
      .catch(() => toast.error('Failed to load Yabbit users'))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = users.filter(u =>
    search === '' ||
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.user.includes(search) ||
    u.department.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (uid) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(filtered.map(u => u.user)));
  const clearAll  = () => setSelected(new Set());

  const handleSave = async () => {
    setSaving(true);
    try {
      const list = Array.from(selected);
      await axios.post(`${API}/yabbit/selected-users`, { selected_users: list });
      toast.success(`${list.length} users saved for sync`);
      onSaved?.(list);
      onClose();
    } catch {
      toast.error('Failed to save selection');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Select Users to Include in Sync
          </DialogTitle>
          <p className="text-sm text-slate-500">
            Only calls from ticked extensions will be imported. {selected.size > 0 && (
              <span className="font-medium text-primary">{selected.size} selected</span>
            )}
          </p>
        </DialogHeader>

        {/* Search + bulk actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, extension or department..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="user-search"
            />
          </div>
          <Button variant="outline" size="sm" onClick={selectAll}>All</Button>
          <Button variant="outline" size="sm" onClick={clearAll}>None</Button>
        </div>

        {/* User list */}
        <div className="flex-1 overflow-y-auto space-y-1 min-h-0 pr-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-slate-400 py-8">No users found</p>
          ) : (
            filtered.map(user => {
              const isSelected = selected.has(user.user);
              return (
                <div
                  key={user.user}
                  onClick={() => toggle(user.user)}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all hover:border-primary/40 ${
                    isSelected ? 'bg-teal-50 border-teal-200' : 'bg-white border-slate-100'
                  }`}
                  data-testid={`user-row-${user.user}`}
                >
                  {isSelected
                    ? <CheckSquare className="w-5 h-5 text-primary flex-shrink-0" />
                    : <Square className="w-5 h-5 text-slate-300 flex-shrink-0" />
                  }
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900 text-sm">{user.name || `Ext ${user.user}`}</span>
                      <span className="text-xs text-slate-400 font-mono">#{user.user}</span>
                    </div>
                    {user.department && (
                      <p className="text-xs text-slate-400">{user.department}</p>
                    )}
                  </div>
                  <Badge className={`text-xs flex-shrink-0 ${SCOPE_COLOR[user.scope] || SCOPE_COLOR['Basic User']}`}>
                    {user.scope}
                  </Badge>
                </div>
              );
            })
          )}
        </div>

        <DialogFooter className="flex-shrink-0 pt-3 border-t">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || loading} data-testid="save-users-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save {selected.size} User{selected.size !== 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default YabbitUsersModal;
